import { useState, useEffect } from "react";
import type { ExperienceSequence } from "@/lib/types";
import { CITY_VISUALS } from "@/lib/experiences/visuals";

interface ItineraryMediaGalleryProps {
  sequence:     ExperienceSequence;
  extraImages?: string[];
}

export function ItineraryMediaGallery({ sequence, extraImages = [] }: ItineraryMediaGalleryProps) {
  const visual  = CITY_VISUALS[sequence.city_iata] ?? null;
  const heroSrc = visual?.heroImage ?? null;

  // Ordered unique candidate list: city hero first, then experience images
  const candidates = Array.from(new Set([
    ...(heroSrc ? [heroSrc] : []),
    ...extraImages.filter(u => typeof u === "string" && u.startsWith("https://")),
  ]));

  const [failedUrls, setFailedUrls] = useState<Set<string>>(new Set());
  const [primaryLoaded, setPrimaryLoaded] = useState(false);

  const validCandidates = candidates.filter(u => !failedUrls.has(u));
  const primarySrc      = validCandidates[0] ?? null;
  const secondaries     = validCandidates.slice(1, 3);

  // Reset all image state when the city changes
  useEffect(() => {
    setPrimaryLoaded(false);
    setFailedUrls(new Set());
  }, [sequence.city_iata]);

  const markPrimaryFailed = (url: string) => {
    setPrimaryLoaded(false);
    setFailedUrls(prev => new Set([...prev, url]));
  };

  return (
    <div
      className="relative overflow-hidden"
      style={{
        height:       "clamp(170px, 44vw, 240px)",
        borderRadius: "8px",
        background:   "oklch(0.920 0.018 72)",
        border:       "1px solid oklch(0.880 0.024 72 / 0.40)",
        flexShrink:   0,
      }}
    >
      {/* Primary photo — key forces remount when URL changes */}
      {primarySrc && (
        <img
          key={primarySrc}
          src={primarySrc}
          alt={primarySrc === heroSrc ? (visual?.caption ?? sequence.city_display) : ""}
          className="absolute inset-0 w-full h-full object-cover"
          style={{
            opacity:        primaryLoaded ? 1 : 0,
            transition:     "opacity 0.85s ease",
            objectPosition: primarySrc === heroSrc
              ? (visual?.objectPosition ?? "center center")
              : "center center",
          }}
          loading="eager"
          onLoad={()  => setPrimaryLoaded(true)}
          onError={() => markPrimaryFailed(primarySrc)}
        />
      )}

      {/* Supporting image tiles — top-right, appear after primary is loaded */}
      {primaryLoaded && secondaries.length > 0 && (
        <div className="absolute top-3 right-3 flex flex-col gap-1.5" aria-hidden>
          {secondaries.map(src => (
            <SecondaryTile
              key={src}
              src={src}
              onFail={() => setFailedUrls(prev => new Set([...prev, src]))}
            />
          ))}
        </div>
      )}

      {/* Gradient scrim */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: "linear-gradient(to top, oklch(0.06 0.012 50 / 0.82) 0%, oklch(0.06 0.012 50 / 0.28) 48%, transparent 76%)",
          opacity:    primaryLoaded ? 1 : 0,
          transition: "opacity 0.85s ease",
        }}
        aria-hidden
      />

      {/* Bottom text overlay */}
      <div className="absolute inset-x-0 bottom-0 z-10 px-5 py-4 sm:px-6 sm:py-5">
        <p
          className="text-[10px] sm:text-[11px] uppercase tracking-[0.24em] font-bold mb-1"
          style={{ color: primaryLoaded ? "rgba(255,255,255,0.50)" : "oklch(0.50 0.06 60 / 0.55)" }}
        >
          {sequence.country}
        </p>
        <h2
          className="font-sans font-light leading-none mb-2.5"
          style={{
            fontSize:      "clamp(1.9rem, 5vw, 2.7rem)",
            letterSpacing: "-0.025em",
            color:         primaryLoaded ? "rgba(255,255,255,0.97)" : "oklch(0.120 0.040 258)",
          }}
        >
          {sequence.city_display}
        </h2>
        <span
          className="inline-flex items-center text-xs font-semibold px-2.5 py-1 rounded-full"
          style={{
            background: primaryLoaded ? "rgba(255,255,255,0.14)" : "oklch(0.716 0.082 74 / 0.12)",
            color:      primaryLoaded ? "rgba(255,255,255,0.92)" : "oklch(0.52 0.10 65)",
            border:     `1px solid ${primaryLoaded ? "rgba(255,255,255,0.22)" : "oklch(0.716 0.082 74 / 0.22)"}`,
          }}
        >
          {sequence.total_days} {sequence.total_days === 1 ? "día" : "días"}
        </span>
        {primaryLoaded && visual?.caption && primarySrc === heroSrc && (
          <p
            className="mt-2 text-[9px] uppercase tracking-[0.14em]"
            style={{ color: "rgba(255,255,255,0.20)" }}
          >
            {visual.caption}
          </p>
        )}
      </div>
    </div>
  );
}

interface SecondaryTileProps {
  src:    string;
  onFail: () => void;
}

function SecondaryTile({ src, onFail }: SecondaryTileProps) {
  const [state, setState] = useState<"idle" | "loaded" | "failed">("idle");

  if (state === "failed") return null;

  return (
    <div
      className="overflow-hidden rounded-md shrink-0"
      style={{
        width:      "clamp(62px, 5.5vw, 80px)",
        height:     "clamp(48px, 4.2vw, 62px)",
        opacity:    state === "loaded" ? 1 : 0,
        transition: "opacity 0.5s ease",
        border:     "1.5px solid rgba(255,255,255,0.18)",
        boxShadow:  "0 2px 10px rgba(0,0,0,0.20)",
      }}
    >
      <img
        src={src}
        alt=""
        aria-hidden
        className="w-full h-full object-cover"
        onLoad={()  => setState("loaded")}
        onError={() => { setState("failed"); onFail(); }}
      />
    </div>
  );
}
